import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onIndexChange,
  onClose
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const swipe = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const swiped = useRef(false);
  const reduceMotion = useReducedMotion();
  const open = index !== null;
  const count = pages.length;
  const step = (dir: 1 | -1) => {
    if (index === null) return;
    onIndexChange((index + dir + count) % count);
  };
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();else if (e.key === 'ArrowRight') onIndexChange((index + 1) % count);else if (e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onIndexChange]);
  const onPointerDown = (e: React.PointerEvent) => {
    swipe.current = {
      x: e.clientX,
      y: e.clientY
    };
    swiped.current = false;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!swipe.current) return;
    const dx = e.clientX - swipe.current.x;
    const dy = e.clientY - swipe.current.y;
    swipe.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      swiped.current = true;
      step(dx < 0 ? 1 : -1);
    }
  };
  const onBackdropClick = () => {
    if (swiped.current) {
      swiped.current = false;
      return;
    }
    onClose();
  };
  const page = index !== null ? pages[index] : null;
  const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full bg-willow text-willow-ink shadow-card transition-colors duration-150 hover:bg-willow-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cream/70';
  return <AnimatePresence data-id="element-209" data-mp="i7znuytk808n">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Page ${index + 1} of ${count}: ${page.caption}`} className="fixed inset-0 z-50 flex touch-pan-y select-none items-center justify-center bg-black/90" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onBackdropClick} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => swipe.current = null} data-id="element-210" data-mp="w668hd9i03qd">
          <p className="absolute left-4 top-4 flex h-11 items-center text-sm font-semibold tabular-nums text-cream" aria-live="polite" data-id="element-211" data-mp="215cqfw0txj1p">
            {index + 1} / {count}
          </p>

          <button ref={closeRef} type="button" onClick={e => {
        e.stopPropagation();
        onClose();
      }} aria-label="Close page preview" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-willow" data-id="element-212" data-mp="2tts166cxhf78">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-213" data-mp="3cqjelz5sh03d" />
          </button>

          <motion.img key={page.src} src={page.src} alt={page.alt} width={1152} height={2048} draggable={false} onClick={e => e.stopPropagation()} className="h-[calc(100dvh-9.5rem)] w-auto max-w-[calc(100vw-2rem)] rounded-xl object-contain shadow-book sm:h-[calc(100dvh-6rem)] sm:max-w-[calc(100vw-10rem)]" initial={reduceMotion ? false : {
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} data-id="element-214" data-mp="3hkdwqy5qx70c" />

          <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-6 sm:static sm:contents" data-id="element-215" data-mp="22ueb0u5kq2bs">
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} aria-label="Previous page" className={`${arrowClass} sm:absolute sm:left-6 sm:top-1/2 sm:-translate-y-1/2`} data-id="element-216" data-mp="1xvl6hr89llnr">
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-217" data-mp="2yv8xoxl3fvoc" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} aria-label="Next page" className={`${arrowClass} sm:absolute sm:right-6 sm:top-1/2 sm:-translate-y-1/2`} data-id="element-218" data-mp="32w1ag0t6cipd">
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-219" data-mp="3hs0nlpv5591d" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}